import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useFetch } from '@reveni-io/reveni-ui'

import { manageCarrierServiceTypes, subscriptionPlans } from '@/common/constants'
import { transformRegionsForMultiSelect } from '@/common/helpers/helpers'
import useCarriers from '@/common/hooks/useCarriers/useCarriers'
import useRegions from '@/common/hooks/useRegions/useRegions'
import commonServices from '@/common/services/services'
import { getCarrierServices } from '@/configuration/pages/ConfigurationPage/helpers'
import { returnMethods } from '@/configuration/pages/ConfigurationPage/LogisticsConfiguration/DeliveryMethods/DeliveryMethodDetailConfiguration/constants'
import {
  getLocationRegionId,
  getSelectedRegionIds,
} from '@/configuration/pages/ConfigurationPage/LogisticsConfiguration/helpers'
import configurationServices from '@/configuration/services/services'
import routes from '@/Router/routes.constants'

import { carrierTypes, initialConfigsByType, initialHomePickUpConfig } from './constants'
import { getCarrierByManageCarrierService, getUpdatedCarrier } from './helpers'
import { transformResponseCarrierServices } from './transformers'

const useDeliveryMethod = ({ id, selectedDeliveryMethod, supportedLanguages, storeId, subscriptionPlan }) => {
  const isDuplicate = id?.includes('duplicate-')
  const isNew = id === 'new'

  const [hasChanges, setHasChanges] = useState(false)
  const [deliveryMethodType, setDeliveryMethodType] = useState(returnMethods.customerpickup)
  const [deliveryMethod, setDeliveryMethod] = useState(initialHomePickUpConfig)
  const [errors, setErrors] = useState([])
  const [manageCarrierService, setManageCarrierService] = useState(manageCarrierServiceTypes.auto)

  const navigate = useNavigate()

  const { regions: regionsData, loading: fetchingRegions } = useRegions()
  const { data: currencies, loading: fetchingCurrencies } = useFetch(commonServices.getCurrencies, {
    doInitialRequest: true,
  })
  const { fetchData: editDeliveryMethod, loading: editingDeliveryMethod } = useFetch(
    configurationServices.editDeliveryMethod,
    {
      doInitialRequest: false,
    }
  )
  const { fetchData: createDeliveryMethod, loading: creatingDeliveryMethod } = useFetch(
    configurationServices.createDeliveryMethod,
    {
      doInitialRequest: false,
    }
  )

  const { carriers, fetchingCarriers, fetchCarriers } = useCarriers({ storeId, doInitialRequest: false })

  const {
    data: carrierServices,
    loading: fetchingCarrierServices,
    fetchData: fetchCarrierServices,
  } = useFetch(commonServices.getCarrierServices, {
    doInitialRequest: false,
    transformResponse: transformResponseCarrierServices,
  })

  const refetchCarriers = useCallback(
    (method, type, manage) => {
      const carrierType = carrierTypes[type]
      const isCustomerPickup = type === returnMethods.customerpickup
      const isPickupPointAutoManage = type === returnMethods.pickuppoint && manage === manageCarrierServiceTypes.auto
      const isPickupPointManualManage =
        type === returnMethods.pickuppoint && manage === manageCarrierServiceTypes.manual

      if (isCustomerPickup || isPickupPointAutoManage) {
        fetchCarrierServices({
          storeId,
          type: carrierType,
          sourceRegion: getSelectedRegionIds(method?.regions),
          destinationRegion: getLocationRegionId(method?.location),
        })
      } else if (isPickupPointManualManage) {
        fetchCarriers({ storeId, type: carrierType })
      }
    },
    [storeId, fetchCarrierServices, fetchCarriers]
  )

  useEffect(() => {
    if (!isNew && selectedDeliveryMethod) {
      const loadedDeliveryMethod = {
        ...selectedDeliveryMethod,
        regions: transformRegionsForMultiSelect(selectedDeliveryMethod.regions),
        availability_criterion_ids: selectedDeliveryMethod.availability_criterion_ids || [],
      }
      const initialManage = selectedDeliveryMethod?.carrier_service?.id
        ? manageCarrierServiceTypes.auto
        : manageCarrierServiceTypes.manual

      setDeliveryMethodType(selectedDeliveryMethod.type)
      setDeliveryMethod(loadedDeliveryMethod)
      refetchCarriers(loadedDeliveryMethod, selectedDeliveryMethod.type, initialManage)

      if (isDuplicate) {
        setHasChanges(true)
      }
    } else if (isNew) {
      refetchCarriers(initialHomePickUpConfig, returnMethods.customerpickup, manageCarrierServiceTypes.auto)
    }
  }, [id, selectedDeliveryMethod, supportedLanguages, isDuplicate, isNew, refetchCarriers])

  useEffect(() => {
    if (subscriptionPlan !== subscriptionPlans.basic) {
      if (deliveryMethod?.carrier_service?.id) {
        setManageCarrierService(manageCarrierServiceTypes.auto)
      } else if (deliveryMethod?.carrier?.id) {
        setManageCarrierService(manageCarrierServiceTypes.manual)
      }
    } else {
      setManageCarrierService(manageCarrierServiceTypes.manual)
    }
  }, [subscriptionPlan, deliveryMethod?.carrier_service?.id, deliveryMethod?.carrier?.id])

  const revertChanges = () => {
    if (isNew || isDuplicate) {
      navigate(routes.configurationPage.logistics.deliveryMethods)

      return
    }

    setHasChanges(false)

    if (selectedDeliveryMethod) {
      setDeliveryMethodType(selectedDeliveryMethod.type)
      setDeliveryMethod({
        ...selectedDeliveryMethod,
        regions: transformRegionsForMultiSelect(selectedDeliveryMethod.regions),
        availability_criterion_ids: selectedDeliveryMethod.availability_criterion_ids || [],
      })
    }
  }

  const handleManageCarrierServiceChange = value => {
    setHasChanges(true)
    setManageCarrierService(value)

    const updatedDeliveryMethod = {
      ...deliveryMethod,
      ...getCarrierByManageCarrierService(value, selectedDeliveryMethod),
    }

    setDeliveryMethod(updatedDeliveryMethod)
    refetchCarriers(updatedDeliveryMethod, deliveryMethodType, value)
  }

  const handleCarrierChange = carrier => {
    setHasChanges(true)
    const isManualService = manageCarrierService === manageCarrierServiceTypes.manual

    const updatedDeliveryMethod = {
      ...deliveryMethod,
      ...getUpdatedCarrier(carrier, deliveryMethod, isManualService),
    }

    setDeliveryMethod(updatedDeliveryMethod)
  }

  const handleChangeDeliveryMethodType = type => {
    setDeliveryMethodType(type)

    if (initialConfigsByType[type]) {
      const newDeliveryMethod = {
        ...initialConfigsByType[type],
        internal_name: deliveryMethod.internal_name,
        name: deliveryMethod.name,
      }

      setDeliveryMethod(newDeliveryMethod)
      refetchCarriers(newDeliveryMethod, type, manageCarrierService)
    }
  }

  const onChangeDeliveryMethod = paramsEdited => {
    const editedFields = typeof paramsEdited === 'function' ? paramsEdited(deliveryMethod) : paramsEdited
    const newDeliveryMethod = { ...deliveryMethod, ...editedFields }

    setDeliveryMethod(newDeliveryMethod)
    setHasChanges(true)

    if ('location' in editedFields || 'regions' in editedFields) {
      refetchCarriers(newDeliveryMethod, deliveryMethodType, manageCarrierService)
    }
  }

  const handleChangeInternalName = e => {
    setDeliveryMethod(prev => ({ ...prev, internal_name: e.target.value }))
    setHasChanges(true)
  }

  return {
    fetchingRegions: fetchingRegions,
    regions: regionsData,
    deliveryMethodType,
    deliveryMethod,
    errors,
    setErrors,
    currencies,
    fetchingCurrencies,
    editDeliveryMethod,
    editingDeliveryMethod,
    createDeliveryMethod,
    creatingDeliveryMethod,
    carrierServices: getCarrierServices(deliveryMethod?.carrier_service, carrierServices),
    carriers: carriers || [],
    fetchingCarrierServices,
    fetchingCarriers,
    manageCarrierService,
    handleCarrierChange,
    handleManageCarrierServiceChange,
    hasChanges,
    setHasChanges,
    revertChanges,
    handleChangeDeliveryMethodType,
    onChangeDeliveryMethod,
    handleChangeInternalName,
  }
}

export default useDeliveryMethod
